import React, { useMemo } from 'react';
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Users, MessageSquare, Star } from "lucide-react";
import { useUserFeedbackResponses } from "@/hooks/api-hooks";
import { useAuth } from "@/contexts/auth-context";
import type { FeedbackResponseRecord } from "@/types";

type FeedbackContributor = {
  id: string
  name: string
  avatar: string | null
  initials: string
  feedbackCount: number
  lastFeedback: number
  role: string
};

export function FeedbackContributorsPage() {
  const { user } = useAuth();
  const { data: feedbackResponsesData, isLoading } = useUserFeedbackResponses();

  const feedbackResponses: FeedbackResponseRecord[] = useMemo(() => {
    if (Array.isArray(feedbackResponsesData?.data)) {
      return feedbackResponsesData.data as FeedbackResponseRecord[];
    }
    return [];
  }, [feedbackResponsesData]);

  // Calculate contributors from feedback responses
  const contributorsMap = new Map<string, FeedbackContributor>();

  feedbackResponses
    .filter((response) =>
      (response.target?.id === user?.id || response.targetId === user?.id) &&
      response.submittedAt &&
      !response.isAnonymous
    )
    .forEach((response) => {
      const contributorId = response.respondent?.id;
      if (contributorId) {
        if (contributorsMap.has(contributorId)) {
          const contributor = contributorsMap.get(contributorId);
          if (contributor) {
            contributor.feedbackCount += 1;
            contributor.lastFeedback = Math.max(
              contributor.lastFeedback,
              new Date(response.submittedAt ?? response.createdAt ?? Date.now()).getTime()
            );
          }
        } else {
          const firstInitial = response.respondent?.firstName?.[0] ?? '';
          const lastInitial = response.respondent?.lastName?.[0] ?? '';
          const initials = `${firstInitial}${lastInitial}` || 'UN';

          contributorsMap.set(contributorId, {
            id: contributorId,
            name: `${response.respondent?.firstName || ''} ${response.respondent?.lastName || ''}`.trim() || "Unknown",
            avatar: response.respondent?.avatar || null,
            initials,
            feedbackCount: 1,
            lastFeedback: new Date(response.submittedAt ?? response.createdAt ?? Date.now()).getTime(),
            role: "Team Member" // Default role since we don't have role data
          });
        }
      }
    });

  const contributors = Array.from(contributorsMap.values())
    .sort((a, b) => b.lastFeedback - a.lastFeedback);

  const anonymousCount = feedbackResponses.filter((response) =>
    (response.target?.id === user?.id || response.targetId === user?.id) &&
    response.submittedAt &&
    response.isAnonymous
  ).length;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading contributors...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/feedback"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Feedback
          </Link>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center">
              <Users className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Feedback Contributors</h1>
              <p className="text-muted-foreground text-lg">
                People who have provided you with feedback
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 px-3 py-1 text-sm font-medium">
              {contributors.length} Contributors
            </Badge>
            {anonymousCount > 0 && (
              <Badge className="bg-gray-100 text-gray-800 border-gray-200 px-3 py-1 text-sm font-medium">
                {anonymousCount} Anonymous
              </Badge>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">{contributors.length}</div>
                  <div className="text-sm text-muted-foreground">Total Contributors</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {contributors.reduce((sum, contributor) => sum + contributor.feedbackCount, 0)}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Feedback Given</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl flex items-center justify-center">
                  <Star className="h-6 w-6 text-gray-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">{anonymousCount}</div>
                  <div className="text-sm text-muted-foreground">Anonymous Feedback</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Empty State */}
        {contributors.length === 0 && anonymousCount === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-muted/30 to-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No contributors yet</h3>
              <p className="text-muted-foreground mb-6">
                Once people provide you with feedback, they will appear here.
              </p>
              <Link to="/feedback">
                <Button>
                  Back to Feedback Dashboard
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Contributors List */}
        {contributors.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Your Feedback Contributors</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {contributors.map((contributor) => (
                  <div
                    key={contributor.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-border/80 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={contributor.avatar ?? undefined} />
                        <AvatarFallback className="bg-blue-100 text-blue-700">
                          {contributor.initials}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium text-foreground">{contributor.name}</div>
                        <div className="text-sm text-muted-foreground">{contributor.role}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-medium text-foreground">
                          {contributor.feedbackCount} feedback{contributor.feedbackCount !== 1 ? 's' : ''}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Last: {new Date(contributor.lastFeedback).toLocaleDateString()}
                        </div>
                      </div>
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                        {contributor.feedbackCount > 5 ? 'Frequent' : contributor.feedbackCount > 2 ? 'Regular' : 'Occasional'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Anonymous Feedback Note */}
        {anonymousCount > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-gray-600" />
                Anonymous Feedback
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                You have received <strong>{anonymousCount}</strong> piece{anonymousCount !== 1 ? 's' : ''} of anonymous feedback.
                These contributors chose to remain anonymous and are not listed above.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default FeedbackContributorsPage
